/**
 * WALL hourly risk tour (#272; design-council PART XI Ruling 2, BINDING).
 *
 * The PART I hour-rhythm given a literal clock: the wall holds the default
 * view most of the hour, then sweeps a config-driven stop list, a few minutes
 * per stop. Stops are CLOCK-ANCHORED, not timer-chained — minute-of-hour
 * windows — which makes the tour PREDICTABLE (the room learns "the :45
 * sweep") and makes duplicated same-URL walls tour in sync BY CONSTRUCTION
 * (tour position is DERIVED state, Ruling 4.5: clock-sourced, identical
 * everywhere, no cross-window messaging).
 *
 *   :45 fire-risk        — FWI heatmap + all-stages wildfires join the hero
 *   :50 flood-community  — hydrometric gauges + vulnerable communities join
 *   :55 weather-model    — the GeoMet radar field joins (run-stamped; the
 *                          simplest honest v1 weather content — a WMS TIME
 *                          loop is an enhancement, not required)
 *
 * A stop changes the LAYER PROFILE ONLY (Ruling 2.2) — the hero frame stays
 * the locked Canada default; frame excursions remain the escalate-to-wall
 * class. RED PREEMPTS THE TOUR, ALWAYS (Ruling 2.3): any unacked urgent (the
 * shared `isUnackedUrgent` predicate) suspends it instantly; TIER 0 buries
 * TIER 3, never the reverse. The engine is STATELESS on purpose: a missed
 * window is missed (never timer-chained into a catch-up), and if the queue
 * quiets mid-window the stop shows for its remainder — its next *scheduled*
 * occurrence, not a deferred replay.
 *
 * Pure data + resolvers — no React, no Cesium — so the schedule, preemption,
 * and card-newness rules are unit-testable with an injected clock
 * (`wallTour.test.ts`).
 */
import { isUnackedUrgent, type QueueHazard, type QueueItem } from './anomalyQueue';

/** What a tour stop turns ON beyond the locked wall base profile — a
 *  WALL_VIEW_LAYERS delta (Ruling 2.1). Every field maps 1:1 to a WallMap
 *  hero-layer prop; absent = the wall base value. */
export interface WallTourLayerDelta {
  /** CWFIS FWI heatmap WMS overlay (fire-risk stop). */
  fwiHeatmap?: boolean;
  /** Widen Reported OC Wildfires to ALL stages + response types (fire-risk
   *  stop — the Fire Risk group's all-reported-wildfires view). */
  allFireStages?: boolean;
  /** Hydrometric gauges dots (flood stop). */
  hydrometricGauges?: boolean;
  /** Vulnerable-communities markers + wellness status (flood stop). */
  communities?: boolean;
  /** Wall WMS overlay keys to show for the dwell (weather stop: the GeoMet
   *  radar field — α/style constraints ride the registry entry itself). */
  wmsOverlays?: readonly string[];
}

export interface WallTourStop {
  /** Stable id — also the operator_actions clear-cursor key (payload.stop). */
  id: string;
  /** Operator-facing stop name for the corner chip ("FIRE"). */
  label: string;
  /** Minute-of-hour the stop begins (clock-anchored, Ruling 2.1). */
  clockOffsetMin: number;
  /** Dwell length in minutes (config-driven; demo may shorten). */
  dwellMin: number;
  /** Queue-item hazard families whose NEW items arm this stop's SITREP card
   *  (Ruling 2.5 "cardDomain"). */
  cardDomains: readonly QueueHazard[];
  /** The layer-profile delta applied for the dwell (Ruling 2.2). */
  layerProfile: WallTourLayerDelta;
}

/**
 * The v1 stop list (Ruling 2.1). Offsets/dwells are config — editing the
 * registry is the tuning knob (`?tour=0` kills the tour entirely for a room).
 */
export const WALL_TOUR_STOPS: readonly WallTourStop[] = [
  {
    id: 'fire-risk',
    label: 'FIRE',
    clockOffsetMin: 45,
    dwellMin: 3,
    // Fire-risk families: fused fire hotspots + pre-fusion camera smoke.
    cardDomains: ['fire', 'smoke'],
    layerProfile: { fwiHeatmap: true, allFireStages: true },
  },
  {
    id: 'flood-community',
    label: 'FLOOD',
    clockOffsetMin: 50,
    dwellMin: 3,
    cardDomains: ['flood'],
    layerProfile: { hydrometricGauges: true, communities: true },
  },
  {
    id: 'weather-model',
    label: 'WEATHER',
    clockOffsetMin: 55,
    dwellMin: 3,
    // CAP weather warnings are the stop's card domain.
    cardDomains: ['weather'],
    layerProfile: { wmsOverlays: ['geomet-radar'] },
  },
];

/** Local minute-of-hour for a wall-clock instant (rooms run on local time —
 *  "the :45 sweep" is what the room's wall clock says). */
export function minuteOfHour(nowMs: number): number {
  return new Date(nowMs).getMinutes();
}

/**
 * The stop whose clock window contains `minute`, or null (default view).
 * A window is [clockOffsetMin, clockOffsetMin + dwellMin) — minute precision;
 * windows that cross :00 wrap (not used by the v1 registry, but the math is
 * honest for a config that does).
 */
export function stopAtMinute(
  minute: number,
  stops: readonly WallTourStop[] = WALL_TOUR_STOPS,
): WallTourStop | null {
  for (const stop of stops) {
    const start = stop.clockOffsetMin;
    const end = start + stop.dwellMin;
    const inWindow =
      end <= 60
        ? minute >= start && minute < end
        : minute >= start || minute < end - 60; // wraps past :00
    if (inWindow) return stop;
  }
  return null;
}

/**
 * The ACTIVE tour stop for an instant — null when the hour-rhythm is in its
 * default-view phase OR the tour is suspended (red preempts, Ruling 2.3).
 * `minuteOverride` is the QA clock seam (`?tourMinute=NN`): verification can
 * force any stop without waiting for :45.
 */
export function activeTourStop(
  nowMs: number,
  opts: {
    stops?: readonly WallTourStop[];
    suspended?: boolean;
    minuteOverride?: number | null;
  } = {},
): WallTourStop | null {
  if (opts.suspended) return null;
  const minute = opts.minuteOverride ?? minuteOfHour(nowMs);
  return stopAtMinute(minute, opts.stops ?? WALL_TOUR_STOPS);
}

/** True when the queue holds ANY unacked urgent — the tour-suspension input
 *  (the SAME shared predicate as the ticker takeover + the Urgent chip, so
 *  the wall's red surfaces can never disagree). */
export function tourSuspended(items: readonly QueueItem[]): boolean {
  return items.some(isUnackedUrgent);
}

/** ":48"-style return-time label for a stop (its window end, wrapped). */
export function tourReturnLabel(stop: WallTourStop): string {
  const end = (stop.clockOffsetMin + stop.dwellMin) % 60;
  return `:${String(end).padStart(2, '0')}`;
}

/** The corner tour chip text (Ruling 2.4): "RISK TOUR · FIRE — returns :48". */
export function tourChipLabel(stop: WallTourStop): string {
  return `RISK TOUR · ${stop.label} — returns ${tourReturnLabel(stop)}`;
}

/**
 * The stop's SITREP-card items (Ruling 2.5): domain items that are NEW —
 * first-seen/latest-message timestamp AFTER the stop's cleared-through cursor
 * — and not acknowledged (an acked item has by definition been reviewed at a
 * desk; PART II re-alarm clears the ack on escalation, so a worsening hazard
 * re-arms the card when its feed stamps the escalation). A null cursor =
 * never cleared → every unacked domain item is new. Live urgents can never
 * appear here: any unacked RL≥3 suspends the whole tour (Ruling 2.7's
 * guarantee is structural).
 *
 * Ice/flood anomalies escalate IN PLACE (their Response Level / class mutate on
 * the open row; first_detected_at never moves), so they carry an escalatedAtIso
 * stamp (#281). An item's recency is therefore the LATEST of first-seen and
 * escalation — an in-place worsening past the cursor re-arms a cleared card even
 * though first_detected_at is stale. Families that don't escalate in place leave
 * escalatedAtIso unset and fall back to timeIso unchanged.
 */
export function newTourCardItems(
  items: readonly QueueItem[],
  stop: WallTourStop,
  cursorIso: string | null,
): QueueItem[] {
  const cursorMs = cursorIso ? Date.parse(cursorIso) : null;
  return items.filter((it) => {
    if (!stop.cardDomains.includes(it.hazard)) return false;
    if (it.acknowledged) return false;
    if (cursorMs == null || Number.isNaN(cursorMs)) return true;
    const detectedMs = Date.parse(it.timeIso);
    const escalatedMs = it.escalatedAtIso ? Date.parse(it.escalatedAtIso) : NaN;
    const t = Math.max(
      Number.isFinite(detectedMs) ? detectedMs : -Infinity,
      Number.isFinite(escalatedMs) ? escalatedMs : -Infinity,
    );
    return Number.isFinite(t) && t > cursorMs;
  });
}

/** Per-stop cleared-through cursors, derived READ-TIME from operator_actions
 *  (the #119 idiom — GET /api/wall-tour/cursors). Missing stop = never cleared. */
export type WallTourCursors = Record<string, string | null>;
